import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PackageStatusBadge } from "@/components/StatusBadge";
import ShareTrackingLink from "@/components/ShareTrackingLink";
import { toast } from "sonner";
import { Search, Eye, Printer, Ban, CheckCircle2, Store, User, Bike, MapPin } from "lucide-react";
import { printQRLabels } from "@/lib/printHelper";

const TIMELINE = [
  { key: "QR_CREATED", label: "QR Code Generated" },
  { key: "PRODUCT_ASSIGNED", label: "Product Assigned" },
  { key: "PACKAGE_INFO_ADDED", label: "Package Information Added" },
  { key: "WAITING_FOR_PICKUP", label: "Waiting for Pickup" },
  { key: "PICKED_UP_BY_RIDER", label: "Picked Up by Rider" },
  { key: "RECEIVED_AT_WAREHOUSE", label: "Received at Warehouse" },
  { key: "SHIPPED_OUT", label: "Shipped Out from Warehouse" },
  { key: "OUT_FOR_LAST_MILE", label: "Picked Up for Last-Mile Delivery" },
  { key: "DELIVERED", label: "Delivered to Customer" },
];

const PROGRESS_ORDER = [
  "WAITING_FOR_PICKUP", "PICKED_UP_BY_RIDER", "RECEIVED_AT_WAREHOUSE",
  "SHIPPED_OUT", "OUT_FOR_LAST_MILE", "DELIVERED",
];

export default function MyPackages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/store/packages", { params: { search: search || undefined } })
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openDetail = async (pkgId) => {
    try {
      const { data } = await api.get(`/store/packages/${pkgId}`);
      setViewing(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onCancel = async (pkg) => {
    if (!confirm(`Cancel package ${pkg.package_id}? This cannot be undone.`)) return;
    try {
      await api.post(`/store/packages/${pkg.package_id}/cancel`);
      toast.success("Package cancelled.");
      load();
      if (viewing?.package_id === pkg.package_id) {
        const { data } = await api.get(`/store/packages/${pkg.package_id}`);
        setViewing(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const printLabel = (pkg) => {
    printQRLabels([{
      qr_image: pkg.qr_image, qr_code_id: pkg.qr_code_id,
      store_name: pkg.store_name, assigned_product_name: pkg.items?.[0]?.item_name,
    }]);
  };

  return (
    <div data-testid="my-packages-page" className="space-y-6">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">/ My Packages</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">My Packages</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="mypkg-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by package ID, receiver…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${rows.length} package${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-slate-100/40 dark:bg-slate-900/40">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-100/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
              <tr>
                <th className="text-left px-5 py-3">QR</th>
                <th className="text-left px-5 py-3">Package ID</th>
                <th className="text-left px-5 py-3">Description</th>
                <th className="text-left px-5 py-3">Items</th>
                <th className="text-left px-5 py-3">Receiver</th>
                <th className="text-left px-5 py-3">Rider</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Created</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const totalQty = (p.items || []).reduce((acc, it) => acc + (it.quantity || 0), 0);
                return (
                  <tr key={p.id} data-testid={`mypkg-row-${p.package_id}`} className="border-b border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-100/60 hover:dark:bg-slate-900/60 transition-colors">
                    <td className="px-5 py-3"><span className="text-cyan-300 font-mono-tech text-xs">{p.qr_code_id || "—"}</span></td>
                    <td className="px-5 py-3 text-cyan-300 font-mono-tech text-xs">{p.package_id}</td>
                    <td className="px-5 py-3 text-slate-200 max-w-[200px] truncate">{p.package_name}</td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{(p.items || []).length} · {totalQty} qty</td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300">
                      <div>{p.receiver_name}</div>
                      <div className="text-xs text-slate-500">{p.receiver_phone}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{p.assigned_rider_name || <span className="text-slate-500 dark:text-slate-600">—</span>}</td>
                    <td className="px-5 py-3"><PackageStatusBadge status={p.status} /></td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-400 font-mono-tech text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          data-testid={`mypkg-view-${p.package_id}`}
                          size="sm" variant="outline"
                          onClick={() => openDetail(p.package_id)}
                          className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                        >
                          <Eye className="w-3 h-3 mr-1" /> Details
                        </Button>
                        {p.qr_code_id && (
                          <ShareTrackingLink qrCodeId={p.qr_code_id} variant="icon" />
                        )}
                        {p.status === "WAITING_FOR_PICKUP" && (
                          <Button
                            data-testid={`mypkg-cancel-${p.package_id}`}
                            size="sm" variant="ghost"
                            onClick={() => onCancel(p)}
                            className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Ban className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-16 text-center text-slate-500">
                  <div className="font-display text-slate-700 dark:text-slate-300 mb-1">No packages yet</div>
                  <div className="text-xs">Create your first package to see it here.</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-3">
              Package Details
              {viewing && <PackageStatusBadge status={viewing.status} />}
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
              {viewing?.package_id}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <PackageDetailContent
              pkg={viewing}
              onCancel={onCancel}
              onPrint={printLabel}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PackageDetailContent({ pkg, onCancel, onPrint }) {
  const reached = useMemo(() => {
    const set = new Set();
    set.add("QR_CREATED");
    if (pkg.qr_assigned_product_id) set.add("PRODUCT_ASSIGNED");
    set.add("PACKAGE_INFO_ADDED");
    const idx = PROGRESS_ORDER.indexOf(pkg.status);
    if (idx >= 0) {
      for (let i = 0; i <= idx; i++) set.add(PROGRESS_ORDER[i]);
    }
    // also add history events
    (pkg.history || []).forEach(h => set.add(h.status));
    return set;
  }, [pkg]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-2">
      <div className="lg:col-span-3 space-y-5">
        <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">/ Package</div>
          <div className="text-lg font-display font-semibold mb-1">{pkg.package_name}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">{pkg.description || "—"}</div>
          {pkg.special_instructions && (
            <div className="mt-3 p-3 rounded-md border border-yellow-500/30 bg-yellow-500/5 text-xs text-yellow-200">
              <b>Instructions:</b> {pkg.special_instructions}
            </div>
          )}
        </section>

        <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">/ Items</div>
          <ul className="space-y-2 text-sm">
            {(pkg.items || []).map((it, i) => (
              <li key={i} className="flex justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-2 last:border-0">
                <span className="text-slate-200">{it.item_name}</span>
                <span className="text-cyan-300 font-mono-tech text-xs">×{it.quantity}</span>
              </li>
            ))}
          </ul>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card icon={Store} label="Store">
            <div className="text-slate-900 dark:text-white font-medium text-sm">{pkg.store_name}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">{pkg.store_owner_name}</div>
          </Card>
          <Card icon={User} label="Receiver">
            <div className="text-slate-900 dark:text-white font-medium text-sm">{pkg.receiver_name}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">{pkg.receiver_phone}</div>
            <div className="text-xs text-slate-500 mt-1 flex items-start gap-1">
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" /> <span>{pkg.receiver_address}</span>
            </div>
          </Card>
          <Card icon={Bike} label="Rider">
            <div className="text-slate-900 dark:text-white font-medium text-sm">{pkg.assigned_rider_name || <span className="text-slate-500">Unassigned</span>}</div>
          </Card>
        </div>

        <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-4">/ Status Timeline</div>
          <ol className="space-y-4">
            {TIMELINE.map((step, idx) => {
              const isReached = reached.has(step.key);
              const hist = (pkg.history || []).find(h => h.status === step.key);
              const isCancelled = pkg.status === "CANCELLED";
              return (
                <li key={step.key} data-testid={`mypkg-timeline-${step.key}`} className="flex items-start gap-3">
                  <div className={`mt-0.5 w-7 h-7 rounded-full grid place-items-center border ${
                    isCancelled && step.key !== "QR_CREATED" && step.key !== "PRODUCT_ASSIGNED" && step.key !== "PACKAGE_INFO_ADDED"
                      ? "bg-red-500/10 border-red-500/40 text-red-400"
                      : isReached
                        ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400"
                        : "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600"
                  }`}>
                    {isReached ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${isReached ? "text-slate-900 dark:text-white" : "text-slate-500"}`}>{step.label}</div>
                    {hist && (
                      <div className="text-[11px] text-slate-500 font-mono-tech mt-0.5">
                        {new Date(hist.created_at).toLocaleString()}
                        {hist.updated_by_role ? ` · ${hist.updated_by_role}` : ""}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
            {pkg.status === "CANCELLED" && (
              <li className="flex items-start gap-3">
                <div className="mt-0.5 w-7 h-7 rounded-full grid place-items-center border bg-red-500/10 border-red-500/40 text-red-400">
                  <Ban className="w-4 h-4" />
                </div>
                <div className="text-sm font-medium text-red-300">Cancelled</div>
              </li>
            )}
          </ol>
        </section>
      </div>

      <div className="lg:col-span-2 space-y-5">
        <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40 text-center">
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">/ QR Code</div>
          <div className="bg-white p-4 rounded-md inline-block">
            {pkg.qr_image && <img src={pkg.qr_image} alt={pkg.qr_code_id} className="w-44 h-44" />}
          </div>
          <div className="mt-3 font-mono-tech text-xs text-slate-600 dark:text-slate-400">{pkg.qr_code_id}</div>
          <Button
            data-testid="mypkg-detail-print"
            onClick={() => onPrint(pkg)}
            className="mt-3 w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            <Printer className="w-4 h-4 mr-2" /> Print Label
          </Button>
          {pkg.status === "WAITING_FOR_PICKUP" && (
            <Button
              data-testid="mypkg-detail-cancel"
              variant="outline"
              onClick={() => onCancel(pkg)}
              className="mt-2 w-full bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Ban className="w-4 h-4 mr-2" /> Cancel Package
            </Button>
          )}
        </section>

        {pkg.qr_code_id && <ShareTrackingLink qrCodeId={pkg.qr_code_id} />}

        <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40 text-xs text-slate-600 dark:text-slate-400 space-y-1.5">
          <div className="flex justify-between"><span className="text-slate-500">Created</span><span>{new Date(pkg.created_at).toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Last Updated</span><span>{new Date(pkg.updated_at).toLocaleString()}</span></div>
          {pkg.delivered_at && (
            <div className="flex justify-between"><span className="text-slate-500">Delivered</span><span className="text-green-300">{new Date(pkg.delivered_at).toLocaleString()}</span></div>
          )}
        </section>

        {pkg.delivery_proof && (
          <section
            data-testid="mypkg-delivery-proof"
            className="border border-green-500/30 rounded-lg p-4 bg-green-500/5"
          >
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-green-300 mb-3">
              / Delivery Proof
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">Package Photo</div>
                <img src={pkg.delivery_proof.proof_photo} alt="Delivery proof" className="w-full rounded-md border border-slate-300 dark:border-slate-700" />
              </div>
              <div>
                <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">Signature</div>
                <div className="bg-white rounded-md p-2">
                  <img src={pkg.delivery_proof.signature_image} alt="Signature" className="w-full max-h-28 object-contain" />
                </div>
              </div>
              <div className="text-xs">
                <div className="text-slate-600 dark:text-slate-400">Signed by: <span className="text-slate-900 dark:text-white">{pkg.delivery_proof.receiver_signed_name || "—"}</span></div>
                <div className="text-slate-500 font-mono-tech text-[10px] mt-0.5">
                  by {pkg.delivery_proof.captured_by_rider_name} · {new Date(pkg.delivery_proof.captured_at).toLocaleString()}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, children }) {
  return (
    <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
      <div className="flex items-center gap-2 mb-2 text-cyan-400">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">{label}</div>
      </div>
      {children}
    </section>
  );
}
